/**
 * The numbers and names the Plants page (src/features/plant-list) is tuned
 * by: delays, limits, sizes used in logic, storage keys and event names.
 *
 * Kept apart from the components so they can be found and changed in one
 * place, and so they can later be read from the database instead (each is
 * given a kept-values group, scripts/kept-values.md). Constants only: no functions.
 *
 * Used by: features/plant-list (the grid, cards, notes, filters, photo and
 * share dialogs), and a few borrowers: the season review and its share
 * frame, the public garden page's parts, and shared/api/diagnose.
 *
 * @module tunables/plantList
 */

// --- Arranging the garden (lib/plantViews) ---

/**
 * Where this browser remembers the Plants page's view and sort (localStorage).
 * The ".v2" started everyone on "Needs you first" once; a new suffix would
 * do the same again for a future default.
 * @kept K1
 */
export const PLANT_VIEW_STORAGE_KEY = 'bloom.plantView.v2';

// --- Photo dialogs ---

/**
 * How long the camera waits before asking for the stream, so the <video> it
 * is about to fill has been painted. Milliseconds.
 * @kept K4
 */
export const CAMERA_PAINT_WAIT_MS = 100;

/**
 * Which camera to ask for: 'environment' is the one on the back of a phone,
 * the one pointed at a plant rather than the gardener.
 * @kept K4
 */
export const CAMERA_FACING_MODE = 'environment';

/**
 * The file name a camera capture is handed on with, before upload renames it.
 * @kept K4
 */
export const CAMERA_CAPTURE_FILE_NAME = 'camera-capture.jpg';

/**
 * How long a downloaded photo's object URL is kept before it is revoked.
 * Revoking straight after the click cancels the save in some browsers.
 * Milliseconds.
 * @kept K4
 */
export const PHOTO_DOWNLOAD_REVOKE_DELAY_MS = 10000;

/**
 * The start of a downloaded photo's file name, before its day and id
 * ("bloom-2026-06-12-41.jpg").
 * @kept K4
 */
export const PHOTO_DOWNLOAD_FILE_PREFIX = 'bloom-';

/**
 * How long the tip shown after adding a plant stays on screen. Milliseconds.
 * @kept K5
 */
export const PLANT_FORM_TIP_TOAST_MS = 8000;

// --- Sharing (SharePlantDialog, shareFrames, shareStyle) ---


/**
 * The finished pictures' sizes, by name: what each platform shows at full
 * resolution. Pixels, width by height.
 * @kept K4
 */
export const SHARE_FRAME_SIZES = {
  square: { w: 1080, h: 1080 },
  // Instagram's tallest feed post (4:5), the size it shows biggest: the collage's own.
  portrait: { w: 1080, h: 1350 },
  story: { w: 1080, h: 1920 },
  pin: { w: 1000, h: 1500 },
} as const;

/**
 * The collage share (layout 'collage'): its first picture lays the chosen
 * photographs out together, then each photograph follows on its own, so a
 * post becomes a carousel. Everything about its look is here, to change
 * without touching the drawing:
 *   ground   - the border colour between and around the photographs;
 *   padPx    - the border around the edge, in pixels of a 1080-wide frame;
 *   gapPx    - the border between photographs, the same way;
 *   radiusPx - each photograph's corner rounding, the same way;
 *   footPx   - the border along the bottom, deeper than the rest, where the
 *              gardener's address (SHARE_SIGNATURE) sits;
 *   slideWords - whether the photographs after the collage carry the usual
 *              lettering, what Details prints (true since 2026-10-15, owner:
 *              "the words that you chose were like date and handle and stuff
 *              applied to the individual pictures too, not just the collage").
 * @dbCandidate
 */
export const SHARE_COLLAGE = {
  ground: '#f4efe4',
  padPx: 18,
  gapPx: 12,
  radiusPx: 4,
  footPx: 46,
  slideWords: true,
} as const;

/**
 * The signature every share's first picture carries, and nothing else of
 * Grow's: the gardener's own Grow address (grow.gardengameplan.com/@amy),
 * small, in the bottom-right corner (on a collage, in its bottom border).
 * No logo, no leaf, no word "Grow" on the picture; that is in the caption.
 *   show     - draw it at all;
 *   sizePx   - its letters' height, in pixels of a 1080-wide frame;
 *   opacity  - 0 to 1;
 *   insetPx  - from the frame's right and bottom edges, the same way;
 *   firstOnly - only on the set's first picture (the rest of a carousel clean).
 * @kept K4
 */
export const SHARE_SIGNATURE = {
  show: true,
  sizePx: 24,
  opacity: 0.92,
  insetPx: 30,
  firstOnly: true,
} as const;

/**
 * The collage's arrangements, by how many photographs it holds (2 to 9; more
 * use the 9's with the first nine). Each tile is [left, top, width, height]
 * as fractions of the area inside the border; the gap is cut from between
 * them when drawn. The largest tile takes the first photograph in the
 * story, the next largest the second, and so on.
 * @kept K4
 */
export const SHARE_COLLAGE_TEMPLATES: Record<number, ReadonlyArray<readonly [number, number, number, number]>> = {
  2: [[0, 0, 1, 0.5], [0, 0.5, 1, 0.5]],
  3: [[0, 0, 0.6, 1], [0.6, 0, 0.4, 0.5], [0.6, 0.5, 0.4, 0.5]],
  4: [[0, 0, 0.5, 0.5], [0.5, 0, 0.5, 0.5], [0, 0.5, 0.5, 0.5], [0.5, 0.5, 0.5, 0.5]],
  5: [[0, 0, 0.62, 0.6], [0.62, 0, 0.38, 0.3], [0.62, 0.3, 0.38, 0.3], [0, 0.6, 0.5, 0.4], [0.5, 0.6, 0.5, 0.4]],
  6: [[0, 0, 0.62, 0.62], [0.62, 0, 0.38, 0.31], [0.62, 0.31, 0.38, 0.31], [0, 0.62, 0.333, 0.38], [0.333, 0.62, 0.334, 0.38], [0.667, 0.62, 0.333, 0.38]],
  7: [[0.278, 0, 0.444, 0.6], [0, 0, 0.278, 0.5], [0.722, 0, 0.278, 0.3], [0.722, 0.3, 0.278, 0.3], [0, 0.5, 0.278, 0.5], [0.278, 0.6, 0.444, 0.4], [0.722, 0.6, 0.278, 0.4]],
  8: [[0.278, 0.24, 0.444, 0.52], [0, 0, 0.278, 0.24], [0.278, 0, 0.444, 0.24], [0.722, 0, 0.278, 0.24], [0, 0.24, 0.278, 0.52], [0.722, 0.24, 0.278, 0.52], [0, 0.76, 0.5, 0.24], [0.5, 0.76, 0.5, 0.24]],
  9: [[0.278, 0.24, 0.444, 0.52], [0, 0, 0.278, 0.24], [0.278, 0, 0.444, 0.24], [0.722, 0, 0.278, 0.24], [0, 0.24, 0.278, 0.52], [0.722, 0.24, 0.278, 0.52], [0, 0.76, 0.278, 0.24], [0.278, 0.76, 0.444, 0.24], [0.722, 0.76, 0.278, 0.24]],
};

/**
 * How much of a story's top, and of its bottom, the app showing it covers
 * with its own furniture (Instagram's account row above, its reply bar
 * below). Pixels of the story's height (SHARE_FRAME_SIZES.story.h).
 * @kept K4
 */
export const SHARE_STORY_SAFE_PX = 250;

/**
 * The text sizes the sharer steps through with A− and A+, smallest first;
 * 1 is as designed. A saved size not in this list falls back to 1.
 * Multipliers.
 * @kept K4
 */
export const SHARE_TEXT_SCALES = [0.8, 1, 1.25, 1.5, 1.8] as const;

/**
 * The lettering's size at text step 1, relative to the frame. Every step
 * scales from it. It was 1, and the words read small on a phone. Multiplier.
 * @kept K4
 */
export const SHARE_TEXT_BASE = 1.25;

/**
 * The share of a Peek frame's width the next photograph gets at its edge.
 * Fraction of the width, 0..1.
 * @kept K4
 */
export const SHARE_PEEK_FRACTION = 0.11;

/**
 * Height over width at or past which a frame counts as tall: a card is drawn
 * wider on it, and Before & after stacks top and bottom rather than side by
 * side. Ratio.
 * @kept K4
 */
export const SHARE_TALL_RATIO = 1.2;

/**
 * The most lines the gardener's words, and the label line, may wrap to on a
 * frame before the last one ends in an ellipsis. Lines.
 * @kept K4
 */
export const SHARE_TEXT_MAX_LINES = 3;

/**
 * The JPEG quality the finished pictures are saved at. 0..1.
 * @kept K4
 */
export const SHARE_JPEG_QUALITY = 0.92;

/**
 * Which of a photo's stored sizes the frames are drawn from: the large file
 * (about 1600px, a few hundred KB; D53). The 800x600 card was enlarged to
 * fill a 1080px frame and looked fuzzy on a computer (owner, 2026-10-15). A
 * photo size name the photo API accepts; a backend before D53 answers
 * "large" with the original, and one without a large copy for a photo
 * answers with its original too.
 * @kept K4
 */
export const SHARE_PHOTO_FETCH_SIZE = 'large';

/**
 * How many fetched photographs are kept for the session, so a share made
 * again reads them from memory: two full shares' worth. Count of photos.
 * @kept K4
 */
export const SHARE_PHOTO_CACHE_SIZE = 24;

/**
 * How many finished pictures are kept while the share sheet is open, so a
 * set made again draws only what changed. Each is a few hundred KB. Count
 * of pictures.
 * @kept K4
 */
export const SHARE_FRAME_CACHE_SIZE = 40;

/**
 * Brightness (0.299 R + 0.587 G + 0.114 B) above which a handle's ink counts
 * as pale and gets a shadow under it. 0..255.
 * @kept K4
 */
export const SHARE_PALE_INK_LUMINANCE = 160;

/**
 * Where the sharer's choices (size, layout, ground, what is printed, text
 * size) are kept on the device. localStorage key.
 * @kept K1
 */
// v2 (2026-10-11): the signature redesign starts everyone on the new defaults (Collage, 4:5, nothing printed but their words).
export const SHARE_STYLE_STORAGE_KEY = 'bloom.shareStyle.v2';

/**
 * How far a press on the preview may wander and still be a tap rather than
 * a drag. Pixels.
 * @kept K4
 */
export const SHARE_TAP_SLOP_PX = 6;

/**
 * How far a gardener may zoom a photograph in its frame on the share
 * screen's Move & zoom: times filling the frame.
 * @kept K4
 */
export const SHARE_ZOOM_MAX = 3;

/**
 * How far a quick sideways flick across the preview must travel to go to the
 * next picture rather than move the crop. Pixels.
 * @kept K4
 */
export const SHARE_SWIPE_PX = 50;

/**
 * How quick a flick across the preview must be; a slower drag repositions
 * the photograph instead. Milliseconds.
 * @kept K4
 */
export const SHARE_SWIPE_MS = 400;

/**
 * How long the sharer's choices must sit still before the pictures are made
 * again, so a run of taps makes one set. Milliseconds.
 * @kept K4
 */
export const SHARE_REBUILD_DELAY_MS = 400;

/**
 * How long the pictures must have been drawing before the preview says
 * "Preparing": a set made in less says nothing, so a tap is not answered
 * with a flash of words. Milliseconds.
 * @kept K4
 */
export const SHARE_PREPARING_DELAY_MS = 1000;

/**
 * The pause between saving one picture of a set and the next, so the browser
 * does not drop or merge the downloads. Milliseconds.
 * @kept K4
 */
export const SHARE_DOWNLOAD_GAP_MS = 400;

/**
 * How long a saved picture's object URL is kept before it is released,
 * long enough for the browser to start the download. Milliseconds.
 * @kept K4
 */
export const SHARE_DOWNLOAD_REVOKE_MS = 10_000;

// --- Plants page (grid, card, notes, toolbar) ---

/**
 * How long after a new plant is saved the garden scrolls to the top, where
 * the plant now leads: long enough for the form to close and let go of the
 * page's scroll. Milliseconds.
 * @kept K5
 */
export const GRID_SCROLL_AFTER_ADD_MS = 350;

/**
 * The width from which the garden has more than one column (Tailwind's md,
 * 768px): cards stop folding and the filter sheet comes from the side. Must
 * stay in step with the md: classes on the grid. A CSS media query.
 * @kept K5
 */
export const PLANTS_WIDE_MEDIA_QUERY = '(min-width: 768px)';

/**
 * Where the weather bar remembers how tall its week's lines were on this
 * device, so the room is held while the brief is on its way and the cards
 * do not move down when it lands (GardenConditionsBar). localStorage.
 * @kept K1
 */
export const WEEK_BAR_HEIGHT_STORAGE_KEY = 'grow.plantsWeek.height.v1';

/**
 * The room the weather bar holds for its lines before this device has seen
 * any: about three lines on a phone. Pixels.
 * @kept K5
 */
export const WEEK_BAR_RESERVED_PX = 128;

/**
 * How many of a plant's journal notes the card shows before "Show N more".
 * Notes.
 * @kept K5
 */
export const NOTES_FOLDED_SHOWN = 1;

/**
 * How close to the note length limit the box starts counting down what is
 * left, so the count reads as a warning rather than decoration. Characters.
 * @kept K5
 */
export const NOTES_COUNTER_WITHIN = 100;

/**
 * How long the gardener's note shortcuts (the quick pills) are trusted
 * before being asked for again. Milliseconds.
 * @kept K1
 */
export const NOTES_SHORTCUTS_STALE_MS = 5 * 60 * 1000;

// --- About a plant (components/card/PlantAboutSheet) ---

// --- A card's photo strip (components/card/PlantCardPhoto) ---

/**
 * A photo's share of the card's frame when the next one peeks in at the
 * edge, as a CSS width; what is left over shows the next picture.
 * @kept K5
 */
export const PHOTO_PEEK_SLIDE_WIDTH = '90%';

/**
 * The gap between photos in the strip, in pixels. Must match the strip's
 * gap-2 class: the swipe position is worked out from it.
 * @kept K5
 */
export const PHOTO_SLIDE_GAP_PX = 8;

/**
 * How far a mouse may move, in pixels, before a press on a photo stops
 * being a click (open the editor) and becomes a drag (swipe).
 * @kept K5
 */
export const PHOTO_DRAG_THRESHOLD_PX = 6;

/**
 * How long, in milliseconds, scroll events are ignored after a dot is
 * tapped, in case the snap lands somewhere other than the tapped photo.
 * @kept K5
 */
export const PHOTO_TAP_SETTLE_MS = 800;

/**
 * How long, in milliseconds, after a mouse drag before the strip snaps to
 * whole photos again, giving its glide to the nearest photo time to land.
 * @kept K5
 */
export const PHOTO_DRAG_SETTLE_MS = 450;


/**
 * How long the app waits for a check-up or a follow-up round, in milliseconds.
 * The server loads several photos, past weather, and the model; that often
 * takes longer than the usual API timeout (REQUEST_TIMEOUT_MS).
 * @kept K1
 */
export const PLANT_DOCTOR_REQUEST_TIMEOUT_MS = 120_000;

/**
 * The plant check-up's status (whether one can be had now, and the
 * week-later questions due): its cache key, and how long it
 * is trusted before it is read again, in milliseconds.
 */
export const PLANT_DOCTOR_STATUS_QUERY_KEY = ['plant-doctor', 'status'] as const;
/** @see PLANT_DOCTOR_STATUS_QUERY_KEY */
export const PLANT_DOCTOR_STATUS_STALE_MS = 5 * 60 * 1000;

/**
 * Where the share in progress is kept on the device while its sheet is open
 * (shareDraft), so a tab Android reloads while the gardener is in another app
 * opens the sheet again where it was.
 */
export const SHARE_DRAFT_STORAGE_KEY = 'bloom.shareDraft.v1';
/**
 * How long a share in progress is put back after the tab reloads, ms: long
 * enough to post in another app and come back. 30 minutes.
 * @kept K4
 */
export const SHARE_DRAFT_KEEP_MS = 30 * 60 * 1000;

/** The most characters in the caption that goes with a share (Instagram's own limit). */
export const SHARE_CAPTION_MAX_CHARS = 2200;
